import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Calendar } from "../ui/calendar";
import { Phone, EnvelopeSimple, PaperPlaneTilt, Clock, CalendarCheck } from "@phosphor-icons/react";
import { useSiteContent } from "../../contexts/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "../../lib/siteContentDefaults";

const TIME_SLOTS = ["9:00 AM", "11:00 AM", "2:00 PM", "5:00 PM"];
const BAY_AREA_TZ = "America/Los_Angeles";

function formatConfirmation(date, slot) {
  if (!date || !slot) return null;
  const weekday = new Intl.DateTimeFormat("en-US", { timeZone: BAY_AREA_TZ, weekday: "long" }).format(date);
  const monthDay = new Intl.DateTimeFormat("en-US", { timeZone: BAY_AREA_TZ, month: "short", day: "numeric" }).format(date);
  const tzAbbr = new Intl.DateTimeFormat("en-US", { timeZone: BAY_AREA_TZ, timeZoneName: "short" })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value;
  return `${weekday}, ${monthDay} @ ${slot} ${tzAbbr}`;
}

export default function BookingSection() {
  const c = { ...DEFAULT_SITE_CONTENT, ...useSiteContent() };
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", type: "contact" });
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const [bookingForm, setBookingForm] = useState({ name: "", email: "", phone: "" });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSent, setBookingSent] = useState(false);

  const confirmationLabel = useMemo(() => formatConfirmation(date, slot), [date, slot]);
  const canSubmit = form.name && form.email && (!date || slot);
  const isBookingReady = !!(date && slot);
  const canConfirmBooking = bookingForm.name && bookingForm.email;

  const handleDateSelect = (d) => {
    setDate(d);
    setSlot(null);
    setBookingSent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please pick a time slot for your selected date");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, preferred_datetime: confirmationLabel });
      setSent(true);
      toast.success("Request received! We'll confirm your call shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmBooking = async (e) => {
    e.preventDefault();
    if (!canConfirmBooking) {
      toast.error("Please share your name and email to confirm");
      return;
    }
    setBookingSubmitting(true);
    try {
      await api.post("/leads", { ...bookingForm, type: "booking", preferred_datetime: confirmationLabel });
      setBookingSent(true);
      toast.success("Your free call is requested!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBookingSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-[#141414] border-y border-[#27272A]">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
        <div>
          <p className="font-heading uppercase tracking-[0.2em] text-xs text-[#D4AF37] mb-2">Book Your Free Call</p>
          <h2 data-testid="booking-title" className="font-heading text-3xl sm:text-4xl md:text-5xl text-white tracking-tight mb-6">{c.booking_title}</h2>
          <p data-testid="booking-subtitle" className="text-[#A1A1AA] mb-8 leading-relaxed">
            {c.booking_subtitle}
          </p>
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3 text-white text-sm"><Phone size={18} className="text-[#D4AF37]" /> (510) 560 5033</div>
            <div className="flex items-center gap-3 text-white text-sm"><EnvelopeSimple size={18} className="text-[#D4AF37]" /> existfit@gmail.com</div>
          </div>
          <div className="border border-[#27272A] rounded-2xl p-4 bg-[#0A0A0A] inline-block">
            <p className="text-xs uppercase tracking-wide text-[#71717A] mb-2 px-2">Pick a Preferred Date</p>
            <Calendar mode="single" selected={date} onSelect={handleDateSelect} data-testid="booking-calendar" className="text-white" />
          </div>

          {date && (
            <div data-testid="booking-time-slots" className="border border-[#27272A] rounded-2xl p-4 bg-[#0A0A0A] mt-4">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#71717A] mb-3 px-1">
                <Clock size={14} /> Available Times (Bay Area / Pacific)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TIME_SLOTS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`booking-time-slot-${s.replace(/[\s:]/g, "-")}`}
                    onClick={() => setSlot(s)}
                    className={`px-2 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${
                      slot === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#D4D4D8] hover:border-[#D4AF37]/50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {confirmationLabel && (
            <div data-testid="booking-confirmation-summary" className="flex items-center gap-3 mt-4 px-4 py-3 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40">
              <CalendarCheck size={20} weight="fill" className="text-[#D4AF37] shrink-0" />
              <p className="text-sm text-white">
                Requesting: <span className="font-semibold text-[#D4AF37]">{confirmationLabel}</span>
              </p>
            </div>
          )}
        </div>

        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl p-8">
          {isBookingReady ? (
            bookingSent ? (
              <div data-testid="booking-success-state" className="h-full flex flex-col items-center justify-center text-center py-16">
                <PaperPlaneTilt size={40} className="text-[#D4AF37] mb-4" />
                <h3 className="font-heading text-2xl text-white tracking-tight mb-2">Call Requested</h3>
                <p className="text-[#A1A1AA] text-sm">
                  We'll see you {confirmationLabel}. We'll confirm your free call within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={confirmBooking} className="space-y-4" data-testid="booking-form">
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#71717A] mb-1">Confirm Your Free Call</p>
                  <p className="text-sm text-[#D4D4D8] mb-4">
                    Requesting <span className="font-semibold text-[#D4AF37]">{confirmationLabel}</span>. Share your details to lock it in.
                  </p>
                </div>
                <input
                  data-testid="booking-confirm-name-input"
                  required
                  placeholder="Full Name"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]"
                />
                <input
                  data-testid="booking-confirm-email-input"
                  type="email"
                  required
                  placeholder="Email Address"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]"
                />
                <input
                  data-testid="booking-confirm-phone-input"
                  placeholder="Phone Number (optional)"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]"
                />
                <button
                  data-testid="booking-confirm-submit-button"
                  disabled={bookingSubmitting || !canConfirmBooking}
                  type="submit"
                  className="w-full bg-[#D4AF37] disabled:opacity-50 text-black font-semibold rounded-full py-3.5 hover:bg-white transition-colors"
                >
                  {bookingSubmitting ? "Confirming..." : "Confirm Booking"}
                </button>
                <button
                  type="button"
                  data-testid="booking-change-slot-button"
                  onClick={() => setSlot(null)}
                  className="w-full text-xs text-[#71717A] hover:text-white transition-colors"
                >
                  Choose a different time
                </button>
              </form>
            )
          ) : sent ? (
            <div data-testid="booking-success-state" className="h-full flex flex-col items-center justify-center text-center py-16">
              <PaperPlaneTilt size={40} className="text-[#D4AF37] mb-4" />
              <h3 className="font-heading text-2xl text-white tracking-tight mb-2">Request Sent</h3>
              <p className="text-[#A1A1AA] text-sm">We'll confirm your free call within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <select
                  data-testid="booking-type-select"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  className="bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                >
                  <option value="contact">General Inquiry</option>
                  <option value="booking">Book Free Call</option>
                  <option value="corporate">Corporate Wellness</option>
                </select>
                <input data-testid="booking-name-input" required placeholder="Full Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-email-input" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
                <input data-testid="booking-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
              </div>
              <textarea data-testid="booking-message-input" placeholder="Tell us about your goals..." rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="w-full bg-[#141414] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
              <button data-testid="booking-submit-button" disabled={submitting || !canSubmit} type="submit" className="w-full bg-[#D4AF37] disabled:opacity-50 text-black font-semibold rounded-full py-3.5 hover:bg-white transition-colors">
                {submitting ? "Sending..." : "Request My Free Call"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

